import type { MouseEvent } from 'react'
import { cn } from '@/lib/cn'
import { useToast } from '@/contexts/ToastContext'
import { ShareIcon } from '@/components/ui/icons'

interface ShareButtonProps {
  title: string
  /** Rota interna do produto; convertida em URL absoluta ao compartilhar. */
  path: string
  className?: string
}

/**
 * Compartilhamento do produto: usa a folha nativa do sistema quando disponível
 * (mobile) e cai para copiar o link na área de transferência.
 */
export function ShareButton({ title, path, className }: ShareButtonProps) {
  const { notify } = useToast()

  async function handleClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    event.stopPropagation()

    const url = new URL(path, window.location.origin).toString()

    if (navigator.share) {
      try {
        await navigator.share({ title, url })
        return
      } catch {
        // Usuário cancelou a folha de compartilhamento: não é erro.
        return
      }
    }

    try {
      await navigator.clipboard.writeText(url)
      notify('Link copiado para a área de transferência.', 'info')
    } catch {
      notify('Não foi possível copiar o link.', 'error')
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`Compartilhar ${title}`}
      title="Compartilhar"
      className={cn(
        'inline-flex size-7 items-center justify-center rounded-full text-brand transition-colors hover:bg-brand-light',
        className,
      )}
    >
      <ShareIcon className="size-4" />
    </button>
  )
}
